import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Website pages and their header/footer statics — mirrors stacks-agents `src/app/api/pages/`. */
@Injectable({ providedIn: 'root' })
export class PagesApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getProjectPages(projectId: string): Promise<Record<string, any>> {
    return this.http
      .get<{ data?: Record<string, any> }>(`${environment.agentsUrl}v4/retrieval/pages`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? {}),
        catchError(() => of({})),
      )
      .toPromise() as Promise<Record<string, any>>
  }

  getProjectPage(projectId: string, pageId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/pages/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, pageId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  getProjectPageStatic(projectId: string, type: 'header' | 'footer'): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/page-statics`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, type },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  /** Upserts a page row (by project_id + page_id). */
  async upsertProjectPage(projectId: string, page: { page_id: string; page_name: string; title: string; undeletable: boolean; all_blocks: any }): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/pages`,
        { projectId, page },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Upserts the header or footer static block data for a project. */
  async upsertProjectPageStatic(projectId: string, type: 'header' | 'footer', blockData: any): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/page-statics`,
        { projectId, type, blockData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Renames a project page. */
  async updateProjectPageName(projectId: string, pageId: string, pageName: string): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/pages/name`,
        { projectId, pageId, pageName },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a page. */
  async deleteProjectPage(projectId: string, pageId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/pages`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, pageId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
